import { Form } from 'antd';
import { useCallback, useMemo, useRef, useState } from 'react';

import { useGetClientServicesQuery } from '@app/api/ClientServices';
import {
  useCreateSubscriptionMutation,
  useEditSubscriptionMutation,
  useLazyGetSubscriptionByIdQuery,
  type ModelSubscription,
} from '@app/api/Subscriptions';
import { FRONT_WARNING, SUCCESS, WARNING } from '@app/constants/alerts';
import { useUserPrint, useWindowSize } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';
import { formatAmount } from '@app/shared/utils/formatAmount';

import { getSubscriptionServicesColumns } from '../data/getSubscriptionServicesColumns';
import {
  fromSubscriptionService,
  getSubscriptionTotals,
  toSubscriptionService,
  withCount,
  withDiscountAmount,
  withDiscountPercent,
} from '../utils/calculations';

import type { SubscriptionFormValues, SubscriptionServiceRow } from '../types';

interface UseSubscriptionModalProps {
  isOpen: boolean;
  /** ID редактируемого абонемента, null — создание нового */
  subscriptionId: number | null;
  onClose: () => void;
}

export const useSubscriptionModal = ({
  isOpen,
  subscriptionId,
  onClose,
}: UseSubscriptionModalProps) => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const { showNotification } = useNotification();
  const { isMobile } = useWindowSize();

  const [form] = Form.useForm<SubscriptionFormValues>();
  const [rows, setRows] = useState<SubscriptionServiceRow[]>([]);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isServicesErrorShown, setIsServicesErrorShown] = useState(false);
  // ID абонемента, которым уже заполнена форма, — чтобы не затирать правки
  // при повторной загрузке данных
  const [filledSubscriptionId, setFilledSubscriptionId] = useState<
    number | null
  >(null);
  // Абонемент из ответа текущей загрузки. data ленивого запроса не подходит:
  // модалка не размонтируется, и там остаётся ответ прошлого открытия.
  const [loadedSubscription, setLoadedSubscription] =
    useState<ModelSubscription | null>(null);
  // Номер последней загрузки — ответы прошлых загрузок игнорируются
  const loadRequestRef = useRef(0);

  const isEditing = subscriptionId !== null;

  const { data: clientServices = [], isFetching: isServicesLoading } =
    useGetClientServicesQuery(
      { ActiveBranchId },
      { skip: !ActiveBranchId || !isOpen }
    );

  // Ленивый запрос: при каждом открытии абонемент грузится заново, и ошибка
  // относится к этой загрузке, а не к закэшированной прошлой.
  const [getSubscriptionById] = useLazyGetSubscriptionByIdQuery();

  const [createSubscription, { isLoading: isCreating }] =
    useCreateSubscriptionMutation();
  const [editSubscription, { isLoading: isEditingSaving }] =
    useEditSubscriptionMutation();

  const isSubscriptionLoading =
    isEditing && filledSubscriptionId !== subscriptionId;

  // Форму заполняем, когда загружены и абонемент, и справочник услуг:
  // из справочника берутся текущая цена и категория услуги.
  const subscriptionToFill = useMemo(() => {
    if (
      !isEditing ||
      !loadedSubscription ||
      loadedSubscription.Id !== subscriptionId ||
      isServicesLoading ||
      filledSubscriptionId === subscriptionId
    ) {
      return null;
    }

    return loadedSubscription;
  }, [
    filledSubscriptionId,
    isEditing,
    isServicesLoading,
    loadedSubscription,
    subscriptionId,
  ]);

  const fillForm = useCallback(
    (data: ModelSubscription) => {
      form.setFieldsValue({
        Name: data.Name ?? '',
        ValidFromPurchaseDays: data.ValidFromPurchaseDays,
      });
      setRows(
        (data.Services ?? []).map((service) =>
          fromSubscriptionService(
            service,
            clientServices.find(({ Id }) => Id === service.ClientServiceId)
          )
        )
      );
      setFilledSubscriptionId(data.Id);
    },
    [clientServices, form]
  );

  // Одну услугу добавляем в абонемент один раз — количество меняется в строке.
  const serviceOptions = useMemo(() => {
    const addedIds = new Set(
      rows.map(({ ClientServiceId }) => ClientServiceId)
    );

    return clientServices
      .filter(({ Id, IsDeleted }) => !IsDeleted && !addedIds.has(Id))
      .map(({ Id, Name, PriceS }) => ({
        value: Id,
        label: `${Name ?? ''} — ${formatAmount(PriceS)} ₽`,
      }));
  }, [clientServices, rows]);

  const totals = useMemo(() => getSubscriptionTotals(rows), [rows]);

  const openPicker = useCallback(() => setIsPickerOpen(true), []);
  const closePicker = useCallback(() => setIsPickerOpen(false), []);

  const addService = useCallback(
    (serviceId: number, count: number) => {
      const service = clientServices.find(({ Id }) => Id === serviceId);

      if (!service) return;

      setRows((prev) => [
        ...prev,
        {
          Id: 0,
          ClientServiceId: service.Id,
          Name: service.Name ?? '',
          CategoryName: service.Category,
          Price: service.PriceS,
          Count: count,
          DiscountAmount: 0,
          DiscountPercent: 0,
        },
      ]);
      setIsPickerOpen(false);
      setIsServicesErrorShown(false);
    },
    [clientServices]
  );

  const updateRow = useCallback(
    (
      serviceId: number,
      update: (row: SubscriptionServiceRow) => SubscriptionServiceRow
    ) => {
      setRows((prev) =>
        prev.map((row) =>
          row.ClientServiceId === serviceId ? update(row) : row
        )
      );
    },
    []
  );

  const changeCount = useCallback(
    (serviceId: number, count: number | null) => {
      updateRow(serviceId, (row) => withCount(row, count ?? 1));
    },
    [updateRow]
  );

  const changeDiscountAmount = useCallback(
    (serviceId: number, amount: number) => {
      updateRow(serviceId, (row) => withDiscountAmount(row, amount));
    },
    [updateRow]
  );

  const changeDiscountPercent = useCallback(
    (serviceId: number, percent: number) => {
      updateRow(serviceId, (row) => withDiscountPercent(row, percent));
    },
    [updateRow]
  );

  const removeService = useCallback((serviceId: number) => {
    setRows((prev) =>
      prev.filter(({ ClientServiceId }) => ClientServiceId !== serviceId)
    );
  }, []);

  const columns = useMemo(
    () =>
      getSubscriptionServicesColumns({
        onChangeCount: changeCount,
        onChangeDiscountAmount: changeDiscountAmount,
        onChangeDiscountPercent: changeDiscountPercent,
        onRemove: removeService,
      }),
    [changeCount, changeDiscountAmount, changeDiscountPercent, removeService]
  );

  const close = useCallback(() => {
    loadRequestRef.current += 1;
    form.resetFields();
    setRows([]);
    setIsPickerOpen(false);
    setIsServicesErrorShown(false);
    setFilledSubscriptionId(null);
    setLoadedSubscription(null);
    onClose();
  }, [form, onClose]);

  // Если абонемент не загрузился, редактировать нечего: показываем ошибку
  // и закрываем модалку.
  const loadSubscription = useCallback(async () => {
    if (!ActiveBranchId || !isOpen || subscriptionId === null) return;

    loadRequestRef.current += 1;
    const requestId = loadRequestRef.current;
    setLoadedSubscription(null);

    try {
      const response = await getSubscriptionById({
        ActiveBranchId,
        subscriptionId,
      }).unwrap();

      if (requestId !== loadRequestRef.current) return;

      setLoadedSubscription(response);
    } catch (error) {
      if (requestId !== loadRequestRef.current) return;

      showNotification(handleCatchError(error), WARNING);
      close();
    }
  }, [
    ActiveBranchId,
    close,
    getSubscriptionById,
    isOpen,
    showNotification,
    subscriptionId,
  ]);

  const submit = useCallback(async () => {
    if (!ActiveBranchId) {
      showNotification('Филиал не был распознан', FRONT_WARNING);
      return;
    }

    const isServicesEmpty = rows.length === 0;
    setIsServicesErrorShown(isServicesEmpty);

    let values: SubscriptionFormValues;

    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    if (isServicesEmpty) return;

    const data: ModelSubscription = {
      Id: subscriptionId ?? 0,
      BranchId: ActiveBranchId,
      Name: values.Name.trim(),
      ValidFromPurchaseDays: values.ValidFromPurchaseDays ?? null,
      SubscriptionCost: totals.SubscriptionCost,
      Services: rows.map(toSubscriptionService),
    };

    try {
      if (isEditing) {
        await editSubscription({ ActiveBranchId, data }).unwrap();
        showNotification('Абонемент сохранён', SUCCESS);
      } else {
        await createSubscription({ ActiveBranchId, data }).unwrap();
        showNotification('Абонемент создан', SUCCESS);
      }

      close();
    } catch (error) {
      showNotification(handleCatchError(error), WARNING);
    }
  }, [
    ActiveBranchId,
    close,
    createSubscription,
    editSubscription,
    form,
    isEditing,
    rows,
    showNotification,
    subscriptionId,
    totals.SubscriptionCost,
  ]);

  return {
    form,
    rows,
    columns,
    totals,
    serviceOptions,
    isServicesLoading,
    isSubmitting: isCreating || isEditingSaving,
    isEditing,
    isSubscriptionLoading,
    subscriptionToFill,
    fillForm,
    loadSubscription,
    isPickerOpen,
    isServicesErrorShown,
    isMobile,
    openPicker,
    closePicker,
    addService,
    close,
    submit,
  };
};
